import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useEscapeStack } from "@/components/hooks/useEscapeStack";
import { InMenu, handleRowNavigation, rowsOf } from "./menuRows";

export type Placement =
  "bottom" | "bottom-end" | "right" | "top" | "top-end" | "top-center";

export type MenuCloseReason = "select" | "escape" | "outside" | "dismiss";

export interface DropdownMenuProps {
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** The surface the gap is measured from, when not the anchor; the menu still centers on the anchor. */
  edgeRef?: React.RefObject<HTMLElement | null>;
  /** Space between the menu and its anchor (or edge); the default is tight. */
  gap?: number;
  /** A point instead of an anchor: a right-click menu. Nothing holds it there,
   *  so a scroll or resize closes it. */
  at?: { x: number; y: number } | null;
  isOpen: boolean;
  onClose: (reason: MenuCloseReason) => void;
  /** Makes it an ARIA menu by this name (`menuitem` rows, arrow keys, focus,
   *  Escape); without one it is a plain surface for pickers and composers. */
  label?: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  placement?: Placement;
  /** Reads the anchor once, when it opens: for a menu whose own acts move it. */
  holdPosition?: boolean;
  /** Outside-mousedowns this returns true for don't close the menu —
   *  e.g. clicks inside a tooltip-portal popover spawned from a row. */
  shouldIgnoreOutsideEvent?: (e: MouseEvent) => boolean;
}

const GAP = 4;

// The last input decides where an opening menu puts focus: a key onto its
// first row, a press onto the panel.
let openedByKeyboard = true;
document.addEventListener("keydown", () => (openedByKeyboard = true), true);
document.addEventListener(
  "pointerdown",
  () => (openedByKeyboard = false),
  true,
);
const SIDE_GAP = 8;
const VIEWPORT_MARGIN = 8;

export interface MenuPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  width?: number;
  maxHeight?: number;
}

/** `start` moved only as far as keeps `length` inside `[margin, room - margin]`. */
const clamp = (start: number, length: number, room: number): number =>
  Math.max(VIEWPORT_MARGIN, Math.min(start, room - length - VIEWPORT_MARGIN));

/** Where a menu of `size` goes: its side when it fits, else the roomier one,
 *  scrolling when neither holds it, never past the viewport's edge. */
export const placeMenu = (
  anchor: DOMRect,
  size: { width: number; height: number },
  placement: Placement | "point",
  viewport = { width: window.innerWidth, height: window.innerHeight },
  gap = GAP,
  /** The dialog the anchor sits in: a side that alone keeps the menu inside it wins. */
  within?: { top: number; bottom: number },
): MenuPosition => {
  const cap = (space: number) =>
    size.height > space ? Math.max(space, 0) : undefined;
  const tallest = viewport.height - 2 * VIEWPORT_MARGIN;
  if (placement === "point")
    return {
      left: clamp(anchor.left, size.width, viewport.width),
      top: clamp(anchor.top, size.height, viewport.height),
      maxHeight: cap(tallest),
    };
  if (placement === "right")
    return {
      top: clamp(
        anchor.top + anchor.height / 2 - size.height / 2,
        size.height,
        viewport.height,
      ),
      left: anchor.right + SIDE_GAP,
      maxHeight: cap(tallest),
    };
  // End-aligned grows leftward from the anchor's right edge, so its width
  // never depends on the room to the right.
  const horizontal: MenuPosition = placement.endsWith("-end")
    ? {
        right: clamp(viewport.width - anchor.right, size.width, viewport.width),
      }
    : placement.endsWith("-center")
      ? {
          left: clamp(
            anchor.left + anchor.width / 2 - size.width / 2,
            size.width,
            viewport.width,
          ),
        }
      : {
          left: clamp(anchor.left, size.width, viewport.width),
          width: anchor.width,
        };
  const below = viewport.height - anchor.bottom - gap - VIEWPORT_MARGIN;
  const above = anchor.top - gap - VIEWPORT_MARGIN;
  // An upward menu goes below only when it does not fit above and below is
  // the roomier side, the mirror of the downward rule.
  const fitsWithin = within && {
    below: size.height <= within.bottom - anchor.bottom - gap,
    above: size.height <= anchor.top - within.top - gap,
  };
  const downward =
    fitsWithin && fitsWithin.below !== fitsWithin.above
      ? fitsWithin.below
      : placement.startsWith("top")
        ? size.height > above && below > above
        : size.height <= below || below >= above;
  return downward
    ? { ...horizontal, top: anchor.bottom + gap, maxHeight: cap(below) }
    : {
        ...horizontal,
        bottom: viewport.height - anchor.top + gap,
        maxHeight: cap(above),
      };
};

const POSITION_KEYS = [
  "top",
  "bottom",
  "left",
  "right",
  "width",
  "maxHeight",
] as const;

const samePosition = (a: MenuPosition | null, b: MenuPosition): boolean =>
  !!a && POSITION_KEYS.every((key) => a[key] === b[key]);

const anchorRectOf = (
  anchorRef: DropdownMenuProps["anchorRef"],
  x: number | undefined,
  y: number | undefined,
  edgeRef?: DropdownMenuProps["edgeRef"],
): DOMRect | undefined => {
  if (x !== undefined && y !== undefined) return new DOMRect(x, y, 0, 0);
  const anchor = anchorRef?.current?.getBoundingClientRect();
  const edge = edgeRef?.current?.getBoundingClientRect();
  return anchor && edge
    ? new DOMRect(anchor.left, edge.top, anchor.width, edge.height)
    : anchor;
};

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  anchorRef,
  edgeRef,
  gap = GAP,
  at,
  isOpen,
  onClose,
  label,
  children,
  className,
  style,
  placement = "bottom",
  holdPosition = false,
  shouldIgnoreOutsideEvent,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const where = at ? "point" : placement;
  const atX = at?.x;
  const atY = at?.y;

  useEscapeStack(isOpen && !!label, () => onCloseRef.current("escape"));

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (shouldIgnoreOutsideEvent?.(e)) return;
      if (
        !anchorRef?.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        onCloseRef.current("outside");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, anchorRef, shouldIgnoreOutsideEvent]);

  // Measured before paint and again whenever the content, the viewport or any
  // scroll container moves the anchor — a menu placed once drifts off it.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!isOpen || !menu) return;
    let held: DOMRect | undefined;
    const place = () => {
      const anchor = held ?? anchorRectOf(anchorRef, atX, atY, edgeRef);
      if (!anchor) return;
      if (holdPosition) held = anchor;
      const borders = menu.offsetHeight - menu.clientHeight;
      const next = placeMenu(
        anchor,
        { width: menu.offsetWidth, height: menu.scrollHeight + borders },
        where,
        undefined,
        gap,
        anchorRef?.current?.closest('[role="dialog"]')?.getBoundingClientRect(),
      );
      setPosition((prev) => (samePosition(prev, next) ? prev : next));
    };
    const dismiss = (e: Event) => {
      if (!(e.target instanceof Node && menu.contains(e.target)))
        onCloseRef.current("dismiss");
    };
    const onMove = where === "point" ? dismiss : place;
    place();
    const observer = new ResizeObserver(place);
    observer.observe(menu);
    if (menu.firstElementChild) observer.observe(menu.firstElementChild);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [isOpen, where, anchorRef, edgeRef, gap, atX, atY, holdPosition]);

  // After a press or at a point the panel takes focus: a pre-highlighted row
  // reads as if the pointer were already on it.
  const atPoint = where === "point";

  // Closes once its anchor, seen, scrolls out of sight, rather than float over
  // what replaced it; a replaced anchor is not a scroll.
  useEffect(() => {
    const anchor = anchorRef?.current;
    if (!isOpen || atPoint || !anchor || !("IntersectionObserver" in window))
      return;
    let seen = false;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) seen = true;
      else if (seen && entry.target.isConnected) onCloseRef.current("dismiss");
    });
    observer.observe(anchor);
    return () => observer.disconnect();
  }, [isOpen, atPoint, anchorRef]);

  useEffect(() => {
    const menu = menuRef.current;
    if (!isOpen || !label || !menu) return;
    opener.current = document.activeElement as HTMLElement | null;
    (openedByKeyboard && !atPoint ? (rowsOf(menu)[0] ?? menu) : menu).focus();
  }, [isOpen, label, atPoint]);

  // Only focus that fell to the body with the menu goes back: a row's action
  // may have moved it on, and an outside press puts it where it pointed.
  useLayoutEffect(() => {
    if (isOpen) return;
    const back = opener.current;
    opener.current = null;
    if (back?.isConnected && document.activeElement === document.body)
      back.focus({ preventScroll: true });
  }, [isOpen]);

  const anchor = isOpen
    ? anchorRectOf(anchorRef, atX, atY, edgeRef)
    : undefined;
  if (!anchor) return null;

  const placed =
    position ??
    placeMenu(anchor, { width: 0, height: 0 }, where, undefined, gap);

  return createPortal(
    <div
      ref={menuRef}
      className={`menu-surface fixed z-9999 ${className ?? ""}`}
      style={{
        ...placed,
        ...(placed.maxHeight !== undefined && { overflowY: "auto" }),
        ...style,
      }}
      {...(label
        ? {
            role: "menu",
            "aria-label": label,
            tabIndex: -1,
            // Through the React tree a menu's keys and clicks would reach its
            // host (a column list deletes on Backspace); Escape goes on.
            onKeyDown: (e: React.KeyboardEvent) => {
              if (handleRowNavigation(e, menuRef.current!)) e.preventDefault();
              if (e.key !== "Escape") e.stopPropagation();
            },
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
            // The pointer moves focus, so it and the arrow keys share one
            // highlight; leaving hands it back to the panel.
            onPointerMove: (e: React.PointerEvent) => {
              const row = (e.target as HTMLElement).closest<HTMLElement>(
                '[role="menuitem"]',
              );
              if (row && row !== document.activeElement)
                row.focus({ preventScroll: true });
            },
            onPointerLeave: () => {
              const menu = menuRef.current!;
              if (menu.contains(document.activeElement))
                menu.focus({ preventScroll: true });
            },
          }
        : {})}
    >
      <InMenu.Provider value={!!label}>{children}</InMenu.Provider>
    </div>,
    document.body,
  );
};

/** A menu beside the row before it: opens right, flips left, shifts up only as
 *  far as the viewport demands. In the parent's DOM, so a press in it is inside. */
export const Submenu: React.FC<{
  label: string;
  /** Only a keyboard open moves focus in; hovering the row must not pull it
   *  away from where the pointer is. */
  autoFocus: boolean;
  onDismiss: () => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ label, autoFocus, onDismiss, style, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState({ flip: false, shift: 0 });

  useLayoutEffect(() => {
    const rect = ref.current!.getBoundingClientRect();
    setPlaced({
      flip: rect.right > window.innerWidth - VIEWPORT_MARGIN,
      shift: Math.max(0, rect.bottom - (window.innerHeight - VIEWPORT_MARGIN)),
    });
  }, []);

  useEffect(() => {
    if (autoFocus && ref.current) rowsOf(ref.current)[0]?.focus();
  }, [autoFocus]);

  // Closing under focus (a pointer leaving, ArrowLeft) hands it to the row.
  useLayoutEffect(() => {
    const panel = ref.current!;
    return () => {
      if (panel.contains(document.activeElement))
        (panel.previousElementSibling as HTMLElement | null)?.focus({
          preventScroll: true,
        });
    };
  }, []);

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      tabIndex={-1}
      className="menu-surface absolute"
      style={{
        top: -placed.shift,
        ...(placed.flip ? { right: "100%" } : { left: "100%" }),
        ...style,
      }}
      // Left collapses this panel alone; Escape closes the whole menu.
      onKeyDown={(event) => {
        const panel = ref.current!;
        if (event.key === "ArrowLeft") onDismiss();
        else if (!handleRowNavigation(event, panel)) return;
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {children}
    </div>
  );
};
